import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, MessageCircle, ThumbsUp, User, LogOut, Calendar, MapPin, Settings } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, status } = useAuth();
  const [myAnswers, setMyAnswers] = useState([]);
  const [stats, setStats] = useState({ answers: 0, upvotes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth check to complete before deciding to redirect
    if (status === "checking") return;
    
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    fetchMyActivity();
  }, [isAuthenticated, status, navigate]);

  const fetchMyActivity = async () => {
    try {
      // Fetch user's answers
      const res = await api.get("/my-answers");
      const answers = res.data || [];
      setMyAnswers(answers);
      
      // Calculate stats
      const totalUpvotes = answers.reduce((sum, a) => sum + (a.upvotes || 0), 0);
      setStats({
        answers: answers.length,
        upvotes: totalUpvotes
      });
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Show loading while checking auth or if not authenticated yet
  if (status === "checking" || !isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      <Seo title="My Profile - AskIncog" />
      
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-slate-100">My Profile</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Card */}
        <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-indigo-500/20">
              {user.name ? user.name.charAt(0).toUpperCase() : "U"}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-100">{user.name || "Anonymous User"}</h2>
              <p className="text-sm text-slate-500">{user.email}</p>
              {user.pseudonym && (
                <p className="text-sm text-indigo-400 mt-1">@{user.pseudonym}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
                <span className="flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-200 font-medium">{stats.answers}</span> answers
                </span>
                <span className="flex items-center gap-1.5">
                  <ThumbsUp className="w-4 h-4 text-emerald-400" />
                  <span className="text-slate-200 font-medium">{stats.upvotes}</span> upvotes
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <Button 
              variant="outline" 
              className="flex-1 border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-slate-100"
              onClick={() => navigate("/settings")}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </Card>

        {/* My Answers Section */}
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-indigo-400" />
            My Answers
          </h3>
          
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : myAnswers.length === 0 ? (
            <Card className="border-slate-700/50 bg-slate-800/30 p-8 text-center">
              <MessageCircle className="w-12 h-12 mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400 mb-4">You haven't answered any questions yet.</p>
              <Button 
                onClick={() => navigate("/browse")}
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                Browse Questions
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {myAnswers.map((answer) => (
                <Link 
                  key={answer.answer_id} 
                  to={`/q/${answer.question_slug}`}
                  className="block"
                >
                  <Card className="border-slate-700/50 bg-slate-800/50 p-4 hover:bg-slate-800 hover:border-indigo-500/30 transition-all">
                    <p className="text-sm text-indigo-400 font-medium mb-1 line-clamp-1">
                      {answer.question_title}
                    </p>
                    <p className="text-slate-300 line-clamp-2 mb-2 text-sm">
                      {answer.answer_text}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3 text-emerald-400" />
                        <span className="text-slate-400">{answer.upvotes || 0}</span>
                      </span>
                      <span>{timeAgo(answer.created_at)}</span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
